import { isDoctor, isNurse } from '@/data/service/authService';
import { useEffect, useRef, useState } from 'react';
import type { SseConnectionStatusMessage } from './protocol';
import type { Listener, SseEventName } from './sse-client';
import { SseClient } from './sse-client';

export interface UseSseOptions {
  events?: { [K in SseEventName]?: Listener<K> };
}

export const useSse = ({ events }: UseSseOptions = {}) => {
  const isClinician = isDoctor() || isNurse();

  const clientRef = useRef<SseClient | undefined>(undefined);

  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<unknown>(undefined);

  useEffect(() => {
    if (!isClinician) {
      return;
    }

    if (!clientRef.current) {
      clientRef.current = new SseClient();
    }

    const onSseConnectionStatus = (message: SseConnectionStatusMessage) => {
      switch (message.status) {
        case 'connected': {
          setIsConnected(true);
          setError(null);
          break;
        }

        case 'disconnected': {
          setIsConnected(false);
          setError(null);
          break;
        }

        case 'error': {
          if (message.detail) {
            setError(message.detail);
          }
          break;
        }
      }
    };

    clientRef.current?.on('sse_connection_status', onSseConnectionStatus);

    return () => {
      clientRef.current?.off('sse_connection_status', onSseConnectionStatus);
      setIsConnected(false);
      setError(null);
    };
  }, [isClinician]);

  useEffect(() => {
    if (!clientRef.current || !events) {
      return;
    }

    Object.entries(events).forEach(([domain, listener]) => {
      clientRef.current?.on(domain as SseEventName, listener as Listener<SseEventName>);
    });

    return () => {
      Object.entries(events).forEach(([domain, listener]) => {
        clientRef.current?.off(domain as SseEventName, listener as Listener<SseEventName>);
      });
    };
  }, [events]);

  return {
    isConnected,
    client: clientRef.current,
    error,
  };
};
